/**
 * Leaderboard auth gate (#285, FR-10.2) — viewing the global leaderboard requires a
 * registered account, so it is no longer an "unnecessary public exposure".
 *
 * Wraps the global top-N board: when registered auth is enabled and the caller is a
 * guest (or has no identity yet), it presents the existing app-owned {@link AuthPanel}
 * behind a "Sign in to see the leaderboard" prompt instead of the board. The panel's
 * full-page resume returns to `/leaderboard` (the stashed `returnTo`), where the
 * now-registered caller falls through to the wrapped children (the board). Mirrors the
 * host gate (#171) so there is no new auth surface (FR-9.5).
 *
 * Scope (D-285.6): this gates ONLY the global board. The per-game summary
 * (`/leaderboard?gameId=`) stays reachable to anyone, so a guest who just finished a
 * game can still see their own standings from the game-over link — the summary section
 * is rendered OUTSIDE this gate in {@link ../pages/LeaderboardPage}.
 *
 * Degrade-aware (L-285.2, D171.2 / D-43.9): when Cognito is not configured
 * (`enabled === false` — local dev / a guest-only deployment) the gate is transparent
 * and renders its children, mirroring the host gate. The read path stays identity-free
 * (D-285.4 = A / FR-10.3): this is a UI gate only, not a server auth check.
 */

import { useAuth } from '../net/use-auth';
import { AuthPanel } from './auth/AuthPanel';

export interface LeaderboardGateProps {
  /** The global leaderboard surface, rendered once viewing is permitted. */
  children: React.ReactNode;
}

export function LeaderboardGate({ children }: LeaderboardGateProps): React.JSX.Element {
  const auth = useAuth();
  // Only block when auth is actually enabled AND the caller is not a registered account.
  // While the identity is still resolving (null) under enabled auth, treat as must-sign-in
  // so we never flash the board to a guest.
  const mustSignIn = auth.enabled && (auth.identity === null || auth.identity.isGuest);

  if (!mustSignIn) return <>{children}</>;

  return (
    <div className="host-gate" data-testid="leaderboard-gate">
      {auth.open ? (
        <AuthPanel auth={auth} />
      ) : (
        <div className="host-gate__prompt stack">
          <h2 className="create-game__title">Sign in to see the leaderboard</h2>
          <p className="create-game__subtitle muted">
            The leaderboard is for players with a free account. Sign in to see the top
            players and your standings.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            data-testid="leaderboard-gate-signin"
            onClick={() => auth.openPanel('signIn')}
          >
            Sign in
          </button>
        </div>
      )}
    </div>
  );
}
